/**
 * The bicycle-wheel backdrop the charity surface sits on.
 *
 * Geometry is held here rather than in the component because the two things
 * worth changing about this backdrop are how many spokes a wheel has and how
 * faint it is, and neither should mean editing render code.
 */

import { SpokeWheel } from "../types/SpokeWheel";
import { SpokeWheelCorner } from "../types/SpokeWheelCornerEnum";

/** Edge length of the square the wheel is drawn in, before it is scaled to `sizePx`. */
export const SPOKE_WHEEL_VIEWBOX_SIZE = 400;

/** Centre of that square, and so the centre of every circle drawn in it. */
export const SPOKE_WHEEL_CENTRE = SPOKE_WHEEL_VIEWBOX_SIZE / 2;

/** Radius of the hub the spokes leave from. */
export const SPOKE_WHEEL_HUB_RADIUS = 26;

/** Radius of the inner rim, the lighter of the two circles at the edge. */
export const SPOKE_WHEEL_INNER_RIM_RADIUS = 172;

/** Radius of the outer rim, drawn heavier so the wheel reads as a tyre. */
export const SPOKE_WHEEL_OUTER_RIM_RADIUS = 188;

/** Where a spoke starts, just clear of the hub's own stroke. */
export const SPOKE_START_RADIUS = 24;

/** Where a spoke ends, just short of the rim, so the joint is not a blob. */
export const SPOKE_END_RADIUS = 184;

/** Stroke width for the spokes and the inner rim. */
export const SPOKE_WHEEL_LINE_WIDTH = 2;

/** Stroke width for the hub. */
export const SPOKE_WHEEL_HUB_WIDTH = 4;

/** Stroke width for the outer rim. */
export const SPOKE_WHEEL_TYRE_WIDTH = 7;

/**
 * The two wheels, and how far each hangs off its corner.
 *
 * Both hubs sit outside the viewport. A wheel whose hub is on screen reads as
 * an illustration of a wheel; one running off the corner reads as texture the
 * page happens to be standing on, which is the whole point of a backdrop.
 *
 * The second wheel is smaller and fainter than the first. Two wheels at one
 * weight read as a pattern, and a pattern competes with the page.
 *
 * Size is capped against viewport width and the offsets are shares of that
 * size, so the composition holds at every width. Fixed pixels looked right on
 * a desktop and wrong on a phone, where a 660px wheel covered most of the
 * screen and ran its spokes straight through the headline.
 */
export const SPOKE_FIELD_WHEELS: readonly SpokeWheel[] = [
  {
    name: "leading",
    spokeCount: 36,
    maxSizePx: 660,
    viewportWidthPercent: 88,
    corner: SpokeWheelCorner.TopRight,
    horizontalOffsetRatio: -0.333,
    verticalOffsetRatio: -0.424,
    opacity: 0.055,
  },
  {
    name: "trailing",
    spokeCount: 28,
    maxSizePx: 520,
    viewportWidthPercent: 70,
    corner: SpokeWheelCorner.BottomLeft,
    horizontalOffsetRatio: -0.442,
    verticalOffsetRatio: -0.577,
    opacity: 0.035,
  },
];
